import type { JSX } from 'react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useImpersonation } from '@/features/auth/hooks/useImpersonation';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { ResultCount } from '@/shared/components/ResultCount';
import { useCandidates } from '../hooks/useCandidates';
import { useDeleteCandidate } from '../hooks/useDeleteCandidate';
import { useUpdateCandidateStatus } from '../hooks/useUpdateCandidateStatus';
import { useResendInvitation } from '../hooks/useResendInvitation';
import { useCopyShareLink } from '../hooks/useCopyShareLink';
import { CandidateList } from '../components/CandidateList';
import { CandidateFilters } from '../components/CandidateFilters';
import { CandidateFormDialog } from '../components/CandidateFormDialog';
import { candidateTableColumns } from '../config/candidateTableColumns';
import type { Candidate, CandidateStatus } from '../types/candidate.types';

export function CandidatesPage(): JSX.Element {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState<Candidate | null>(null);

  const { effectiveCompanyId } = useImpersonation();
  const companyId = effectiveCompanyId;

  const { candidates, hasData, isLoading, isError } = useCandidates({
    companyId: companyId ?? '',
  });
  const {
    filteredSorted,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    sort,
    toggleSort,
    activeFilterCount,
  } = useTableFilters(candidates, candidateTableColumns);
  const { deleteCandidate } = useDeleteCandidate();
  const { updateStatus } = useUpdateCandidateStatus();
  const { resendInvitation } = useResendInvitation();
  const { copyLink } = useCopyShareLink();

  const handleDelete = (id: string): void => {
    deleteCandidate(id, {
      onSuccess: () => toast.success('Candidate deleted successfully'),
      onError: () => toast.error('Failed to delete candidate'),
    });
  };

  const handleStatusChange = (id: string, status: CandidateStatus): void => {
    updateStatus(
      { id, status },
      {
        onSuccess: () => toast.success('Candidate status updated successfully'),
        onError: () => toast.error('Failed to update candidate status'),
      }
    );
  };

  const handleResendInvitation = (id: string): void => {
    resendInvitation(id, {
      onSuccess: () => toast.success('Invitation email resent successfully'),
      onError: () => toast.error('Failed to resend invitation'),
    });
  };

  const handleCopyLink = (shareId: string): void => {
    copyLink(shareId);
  };

  if (!companyId) {
    return (
      <div className="container py-6">
        <div className="text-center py-8 text-destructive">
          No company associated with your account.
        </div>
      </div>
    );
  }

  return (
    <div className="container py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">Candidates</h1>
          {!isLoading && !(isError && !hasData) && <ResultCount count={filteredSorted.length} />}
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Candidate
        </Button>
      </div>

      <CandidateFilters
        filters={filters}
        activeFilterCount={activeFilterCount}
        onFilterChange={setFilter}
        onClearFilters={clearFilters}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />

      {isError && (
        <div className="text-center py-8 text-destructive">
          An error occurred while loading candidates
        </div>
      )}

      <CandidateList
        candidates={filteredSorted}
        isLoading={isLoading}
        sort={sort}
        onToggleSort={toggleSort}
        onEdit={setEditingCandidate}
        onDelete={handleDelete}
        onStatusChange={handleStatusChange}
        onResendInvitation={handleResendInvitation}
        onCopyLink={handleCopyLink}
      />

      <CandidateFormDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        mode="create"
      />

      <CandidateFormDialog
        open={!!editingCandidate}
        onOpenChange={(open) => !open && setEditingCandidate(null)}
        mode="edit"
        candidate={editingCandidate}
      />
    </div>
  );
}
